import { useEffect, useState, useCallback, useMemo, memo, useRef, startTransition } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, Checkbox, Chip, Collapse, InputAdornment,
  CircularProgress, Pagination
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, CheckCircle, Warning, SwapHoriz, History, ExpandMore, ExpandLess, Search, List } from '@mui/icons-material';
import LoadingOverlay from '../../common/LoadingOverlay';

// PROMAX PERFORMANCE: Web Worker for ultra-fast calculations
const createCalculationWorker = () => {
  const workerCode = `
    self.onmessage = function(e) {
      const { details, freightAmount, packingAmount, freightGSTRate, packingGSTRate } = e.data;
      
      // Ultra-fast calculation using optimized loops
      let amount = 0, discountAmount = 0, taxableAmount = 0;
      let cgstAmount = 0, sgstAmount = 0, igstAmount = 0, totalAmount = 0;
      let maxGSTRate = 0;
      
      // Single loop optimization for maximum performance
      for (let i = 0; i < details.length; i++) {
        const d = details[i];
        const amt = parseFloat(d.Amount) || 0;
        const disc = parseFloat(d.DiscountAmount) || 0;
        const tax = parseFloat(d.TaxableAmount) || 0;
        const cgst = parseFloat(d.CGSTAmount) || 0;
        const sgst = parseFloat(d.SGSTAmount) || 0;
        const igst = parseFloat(d.IGSTAmount) || 0;
        const total = parseFloat(d.TotalAmount) || 0;
        const gstRate = parseFloat(d.GSTRate) || 0;
        
        amount += amt;
        discountAmount += disc;
        taxableAmount += tax;
        cgstAmount += cgst;
        sgstAmount += sgst;
        igstAmount += igst;
        totalAmount += total;
        
        if (gstRate > maxGSTRate) maxGSTRate = gstRate;
      }

      const effectiveFreightGSTRate = Math.max(freightGSTRate, maxGSTRate);
      const effectivePackingGSTRate = Math.max(packingGSTRate, maxGSTRate);
      const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
      const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;
      const grandTotal = totalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount;

      self.postMessage({
        Amount: amount,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        CGSTAmount: cgstAmount,
        SGSTAmount: sgstAmount,
        IGSTAmount: igstAmount,
        TotalAmount: Math.round(grandTotal),
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: freightGSTAmount,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: packingGSTAmount,
        RoundOff: Math.round(grandTotal) - grandTotal
      });
    };
  `;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

// PROMAX PERFORMANCE: Immediate UI update TableRow with zero delay
const TableRow = memo(({
  detail,
  index,
  items,
  isViewMode,
  onDetailChange,
  onItemSelect,
  onRemoveRow,
  formatCurrency,
  disabledFieldStyle,
  tinyInput,
  tinyInputCenter
}) => {
  // Local state for immediate UI updates
  const [localDetail, setLocalDetail] = useState(detail);

  // Immediate UI update without waiting for parent state
  const handleImmediateChange = useCallback((field, value) => {
    // Update local state immediately for instant UI feedback
    setLocalDetail(prev => {
      const updated = { ...prev, [field]: value };

      // Calculate immediately for instant display
      if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
        const quantity = Math.max(0, parseFloat(updated.Quantity) || 0);
        const rate = Math.max(0, parseFloat(updated.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(updated.GSTRate) || 0);
        const amount = quantity * rate;

        let discountAmount, discountPct;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
        } else {
          discountPct = Math.max(0, parseFloat(updated.DiscountPercentage) || 0);
          discountAmount = (amount * discountPct) / 100;
        }

        const taxableAmount = amount - discountAmount;
        const gstAmount = (taxableAmount * gstRate) / 100;

        return {
          ...updated,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          CGSTAmount: gstAmount / 2,
          SGSTAmount: gstAmount / 2,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + gstAmount
        };
      }

      return updated;
    });

    // Notify parent in next frame to prevent blocking
    requestAnimationFrame(() => {
      onDetailChange(index, field, value);
    });
  }, [index, onDetailChange]);

  // Sync with parent when detail changes
  useEffect(() => {
    setLocalDetail(detail);
  }, [detail]);

  // Pre-computed values for instant display
  const itemName = useMemo(() => {
    return localDetail.ItemName || items.find(i => i.Id === parseInt(localDetail.ItemId))?.Name || '-';
  }, [localDetail.ItemName, localDetail.ItemId, items]);

  const gstAmount = useMemo(() => {
    return (parseFloat(localDetail.CGSTAmount) || 0) +
      (parseFloat(localDetail.SGSTAmount) || 0) +
      (parseFloat(localDetail.IGSTAmount) || 0);
  }, [localDetail.CGSTAmount, localDetail.SGSTAmount, localDetail.IGSTAmount]);

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
      <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500 }}>{index + 1}</td>
      <td style={{ padding: '4px' }}>
        {isViewMode ? (
          <TextField
            size="small"
            value={itemName}
            disabled
            sx={{ ...tinyInputCenter, width: '100%', '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'left' } }}
          />
        ) : (
          <FormControl fullWidth size="small">
            <Select
              value={localDetail.ItemId || ''}
              onChange={(e) => onItemSelect(index, e.target.value)}
              disabled={isViewMode}
              displayEmpty
              sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5 } }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
            >
              <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}>Select</MenuItem>
              {items.slice(0, 100).map(item => (
                <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                  {item.Name}
                </MenuItem>
              ))}
              {items.length > 100 && (
                <MenuItem disabled sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#6b7280' }}>
                  ... and {items.length - 100} more items. Use "Get Items" for full list.
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          value={localDetail.HSNCode || ''}
          onChange={(e) => handleImmediateChange('HSNCode', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInputCenter, width: '100%' }}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.Quantity || ''}
          onChange={(e) => handleImmediateChange('Quantity', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInputCenter, width: '100%' }}
          inputProps={{ min: 0 }}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.Rate || ''}
          onChange={(e) => handleImmediateChange('Rate', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInput, width: '100%' }}
          inputProps={{ min: 0 }}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.DiscountPercentage ?? 0}
          onChange={(e) => handleImmediateChange('DiscountPercentage', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInputCenter, width: '100%' }}
          inputProps={{ min: 0, max: 100 }}
          onFocus={(e) => e.target.select()}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.DiscountAmount ?? 0}
          onChange={(e) => handleImmediateChange('DiscountAmount', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInput, width: '100%' }}
          inputProps={{ min: 0 }}
          onFocus={(e) => e.target.select()}
        />
      </td>
      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 500 }}>
        {formatCurrency(localDetail.TaxableAmount)}
      </td>
      <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500, fontSize: '0.8rem' }}>
        {localDetail.GSTRate || 0}
      </td>
      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700, color: '#1B5E20' }}>
        {formatCurrency(gstAmount)}
      </td>
      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
        {formatCurrency(localDetail.TotalAmount)}
      </td>
      {!isViewMode && (
        <td style={{ padding: '4px', textAlign: 'right' }}>
          <IconButton size="small" onClick={() => onRemoveRow(index)} sx={{ p: 0.25, color: '#ef4444' }}>
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </td>
      )}
    </tr>
  );
});

const PurchaseOrderModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  orderDetails,
  setOrderDetails,
  onSave,
  onVerify,
  onConvert,
  suppliers,
  items,
  loading,
  freightPackingGST,
  onFetchPOHistory,
  onFetchItems
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State declarations
  const [modalLoading, setModalLoading] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [itemsModalPage, setItemsModalPage] = useState(1);
  const [itemsModalPerPage] = useState(20);
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [itemsPerPage] = useState(50);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [poHistoryModalOpen, setPoHistoryModalOpen] = useState(false);
  const [poHistoryData, setPoHistoryData] = useState([]);
  const [poHistoryLoading, setPoHistoryLoading] = useState(false);
  const [selectedHistoryItems, setSelectedHistoryItems] = useState([]);
  const [expandedHistoryPOId, setExpandedHistoryPOId] = useState(null);
  const [calculationTimeout, setCalculationTimeout] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Purchase Order' : modalAction === 'edit' ? 'Edit Purchase Order' : 'View Purchase Order';

  // PROMAX PERFORMANCE: Web Worker for calculations
  const calculationWorkerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const calculationTimeoutRef = useRef(null);
  const orderDetailsRef = useRef(orderDetails);
  const formDataRef = useRef(formData);

  // Initialize Web Worker
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    if (!calculationWorkerRef.current) {
      calculationWorkerRef.current = createCalculationWorker();

      calculationWorkerRef.current.onmessage = (e) => {
        if (isMountedRef.current) {
          const calculatedTotals = e.data;
          startTransition(() => {
            setFormData(prev => ({ ...prev, ...calculatedTotals }));
          });
        }
      };
    }

    return () => {
      isMountedRef.current = false;
      if (calculationWorkerRef.current) {
        calculationWorkerRef.current.terminate();
        calculationWorkerRef.current = null;
      }
    };
  }, []);

  // Update refs when props change
  useEffect(() => {
    orderDetailsRef.current = orderDetails;
    formDataRef.current = formData;
  }, [orderDetails, formData]);

  // PROMAX PERFORMANCE: Web Worker-based calculations with zero main thread blocking
  const performCalculations = useCallback((details) => {
    if (!calculationWorkerRef.current || details.length === 0) {
      return;
    }

    const currentFormData = formDataRef.current;
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate || freightPackingGST?.freight?.GstRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate || freightPackingGST?.packing?.GstRate) || 0;
    const freightAmount = parseFloat(currentFormData.FreightAmount) || 0;
    const packingAmount = parseFloat(currentFormData.PackingAmount) || 0;

    // Send to Web Worker for non-blocking calculation
    calculationWorkerRef.current.postMessage({
      details,
      freightAmount,
      packingAmount,
      freightGSTRate: defaultFreightGSTRate,
      packingGSTRate: defaultPackingGSTRate
    });
  }, [freightPackingGST]);

  // Helper function to check if IGST should be used
  const isIGSTInvoice = useCallback(() => {
    const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
    return invoiceType.includes('igst');
  }, [formData.InvoiceTypeName]);

  // Fetch PO history when supplier is selected
  const fetchPOHistoryForSupplier = useCallback(async (supplierId) => {
    if (!onFetchPOHistory) return;

    setPoHistoryLoading(true);
    setExpandedHistoryPOId(null);
    try {
      const historyData = await onFetchPOHistory(supplierId);
      if (historyData && historyData.length > 0) {
        setPoHistoryData(historyData);

        // Pre-select items that are already in orderDetails
        const currentOrderDetails = orderDetailsRef.current;
        const alreadyAddedItems = [];
        historyData.forEach(po => {
          (po.PurchaseOrderDetails || []).forEach(historyItem => {
            // Check if this item is already in orderDetails (by ItemId)
            const isAlreadyAdded = currentOrderDetails.some(
              detail => {
                const detailItemId = detail.ItemId || detail.Id;
                return parseInt(detailItemId) === parseInt(historyItem.ItemId);
              }
            );
            if (isAlreadyAdded) {
              alreadyAddedItems.push({ ...historyItem, poId: po.Id });
            }
          });
        });
        setSelectedHistoryItems(alreadyAddedItems);

        setPoHistoryModalOpen(true);
      } else {
        setPoHistoryData([]);
        setSelectedHistoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching PO history:', error);
      setPoHistoryData([]);
      setSelectedHistoryItems([]);
    }
    setPoHistoryLoading(false);
  }, [onFetchPOHistory]);

  // Handle form field changes
  const handleChange = useCallback((field, value) => {
    if (field === 'SupplierId') {
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(value));
      const invoiceTypeName = selectedSupplier?.InvoiceTypeName || selectedSupplier?.InvoiceType || '';
      setFormData(prev => ({ ...prev, [field]: value, InvoiceTypeName: invoiceTypeName }));

      // Fetch PO history for the selected supplier (only in create mode)
      if (modalAction === 'create' && value && onFetchPOHistory) {
        fetchPOHistoryForSupplier(value);
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, [suppliers, modalAction, onFetchPOHistory, fetchPOHistoryForSupplier]);

  // Memoized set of already added item IDs for better performance
  const addedItemIds = useMemo(() => {
    if (!orderDetails || orderDetails.length === 0) return new Set();

    return new Set(orderDetails.map(detail => {
      const itemId = detail.ItemId || detail.Id;
      return parseInt(itemId);
    }).filter(id => !isNaN(id)));
  }, [orderDetails.length, orderDetails.map(d => d.ItemId || d.Id).join(',')]);

  // Check if item is already added to order (optimized with Set lookup)
  const isItemAlreadyInOrder = useCallback((itemId) => {
    return addedItemIds.has(parseInt(itemId));
  }, [addedItemIds]);

  // Toggle item selection in history modal
  const toggleHistoryItemSelection = (item, poId) => {
    const itemKey = `${poId}-${item.ItemId}`;
    setSelectedHistoryItems(prev => {
      const exists = prev.find(i => `${i.poId}-${i.ItemId}` === itemKey);
      if (exists) {
        return prev.filter(i => `${i.poId}-${i.ItemId}` !== itemKey);
      } else {
        return [...prev, { ...item, poId }];
      }
    });
  };

  // Check if item is selected
  const isHistoryItemSelected = (item, poId) => {
    const itemKey = `${poId}-${item.ItemId}`;
    return selectedHistoryItems.some(i => `${i.poId}-${i.ItemId}` === itemKey);
  };

  // Get all items from history for select all functionality
  const getAllHistoryItems = () => {
    const allItems = [];
    poHistoryData.forEach(po => {
      (po.PurchaseOrderDetails || []).forEach(item => {
        allItems.push({ ...item, poId: po.Id });
      });
    });
    return allItems;
  };

  // Select/Deselect all items
  const toggleSelectAllHistoryItems = () => {
    const allItems = getAllHistoryItems();
    if (selectedHistoryItems.length === allItems.length) {
      setSelectedHistoryItems([]);
    } else {
      setSelectedHistoryItems(allItems);
    }
  };

  // Get all items from a specific PO for bill-wise select
  const getPOItems = (poId) => {
    const po = poHistoryData.find(p => p.Id === poId);
    if (!po) return [];
    return (po.PurchaseOrderDetails || []).map(item => ({ ...item, poId }));
  };

  // Check if all items from a specific PO are selected
  const isPOFullySelected = (poId) => {
    const poItems = getPOItems(poId);
    if (poItems.length === 0) return false;
    return poItems.every(item => isHistoryItemSelected(item, poId));
  };

  // Check if some (but not all) items from a specific PO are selected
  const isPOPartiallySelected = (poId) => {
    const poItems = getPOItems(poId);
    if (poItems.length === 0) return false;
    const selectedCount = poItems.filter(item => isHistoryItemSelected(item, poId)).length;
    return selectedCount > 0 && selectedCount < poItems.length;
  };

  // Toggle select all items from a specific PO (bill-wise select)
  const togglePOSelection = (poId) => {
    const poItems = getPOItems(poId);
    if (isPOFullySelected(poId)) {
      // Deselect all items from this PO
      setSelectedHistoryItems(prev =>
        prev.filter(item => item.poId !== poId)
      );
    } else {
      // Select all items from this PO
      setSelectedHistoryItems(prev => {
        // Remove any existing items from this PO first, then add all
        const otherItems = prev.filter(item => item.poId !== poId);
        return [...otherItems, ...poItems];
      });
    }
  };

  // Add selected items from history to order details
  const addSelectedHistoryItems = () => {
    if (selectedHistoryItems.length === 0) return;

    const useIGST = isIGSTInvoice();

    // Filter out items that are already in orderDetails (avoid duplicates)
    const itemsToAdd = selectedHistoryItems.filter(
      historyItem => !orderDetails.some(detail => {
        const detailItemId = detail.ItemId || detail.Id;
        return parseInt(detailItemId) === parseInt(historyItem.ItemId);
      })
    );

    if (itemsToAdd.length === 0) {
      // All selected items are already in the order, just close the modal
      setPoHistoryModalOpen(false);
      setSelectedHistoryItems([]);
      setExpandedHistoryPOId(null);
      return;
    }

    const newItems = itemsToAdd.map(historyItem => {
      const quantity = parseFloat(historyItem.Quantity) || 0;
      const rate = parseFloat(historyItem.Rate) || 0;
      const gstRate = parseFloat(historyItem.GSTRate) || 0;
      const discountPct = parseFloat(historyItem.DiscountPercentage) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;

      return {
        Id: 0,
        ItemId: historyItem.ItemId,
        HSNCode: historyItem.HSNCode || '',
        GSTRate: gstRate,
        Quantity: quantity,
        Rate: rate,
        Amount: amount,
        DiscountPercentage: discountPct,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount,
        IsActive: true
      };
    });

    const updatedDetails = [...orderDetails, ...newItems];
    setOrderDetails(updatedDetails);
    performCalculations(updatedDetails);

    // Close history modal and reset
    setPoHistoryModalOpen(false);
    setSelectedHistoryItems([]);
    setExpandedHistoryPOId(null);
  };

  // Close history modal without adding items
  const closeHistoryModal = () => {
    setPoHistoryModalOpen(false);
    setSelectedHistoryItems([]);
    setExpandedHistoryPOId(null);
  };

  // Handle item search with debouncing
  const handleItemSearch = (searchValue) => {
    setItemSearchValue(searchValue);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search (300ms delay)
    searchTimeoutRef.current = setTimeout(async () => {
      if (onFetchItems) {
        await onFetchItems(searchValue);
      }
    }, 300);
  };

  // Handle Get Items modal
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      if (onFetchItems) {
        await onFetchItems('');
      } else {
        console.warn('onFetchItems function not provided');
      }
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.PurchaseRate || item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Add selected items to order details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const useIGST = isIGSTInvoice();

      const itemsToAdd = selectedItems.filter(
        selectedItem => !addedItemIds.has(parseInt(selectedItem.Id))
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < itemsToAdd.length; i += chunkSize) {
        chunks.push(itemsToAdd.slice(i, i + chunkSize));
      }

      let allNewItems = [];

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];

        const chunkItems = chunk.map(selectedItem => {
          const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
          const rate = parseFloat(selectedItem.selectedRate) || 0;
          const gstRate = parseFloat(selectedItem.GSTRate) || 0;
          const discountPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
          const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
          const amount = quantity * rate;

          const finalDiscountAmount = discountAmt > 0 ? discountAmt : (amount * discountPct) / 100;
          const taxableAmount = amount - finalDiscountAmount;
          const gstAmount = (taxableAmount * gstRate) / 100;

          return {
            Id: 0,
            ItemId: selectedItem.Id,
            HSNCode: selectedItem.HSNCode || '',
            GSTRate: gstRate,
            Quantity: quantity,
            Rate: rate,
            Amount: amount,
            DiscountPercentage: discountAmt > 0 ? (finalDiscountAmount / amount) * 100 : discountPct,
            DiscountAmount: finalDiscountAmount,
            TaxableAmount: taxableAmount,
            CGSTAmount: useIGST ? 0 : gstAmount / 2,
            SGSTAmount: useIGST ? 0 : gstAmount / 2,
            IGSTAmount: useIGST ? gstAmount : 0,
            TotalAmount: taxableAmount + gstAmount,
            IsActive: true
          };
        });

        allNewItems = [...allNewItems, ...chunkItems];

        if (itemsToAdd.length > 100 && chunkIndex < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      const updatedDetails = [...orderDetails, ...allNewItems];
      setOrderDetails(updatedDetails);

      handleCloseGetItemsModal();

      setTimeout(() => {
        performCalculations(updatedDetails);
      }, 0);

    } catch (error) {
      console.error('Error adding selected items:', error);
    } finally {
      setAddingItemsLoading(false);
    }
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Memoized filtered items for better performance
  const filteredItems = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    if (!getItemsSearchValue.trim()) {
      return items;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm)
    );
  }, [items, getItemsSearchValue]);

  // Get paginated items for performance
  const getPaginatedItems = useMemo(() => {
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  // Calculate total pages
  const getTotalPages = useMemo(() => {
    return Math.ceil(filteredItems.length / itemsPerPage);
  }, [filteredItems.length, itemsPerPage]);

  // Pagination for "See More" modal
  const getPaginatedOrderDetails = useMemo(() => {
    const startIndex = (itemsModalPage - 1) * itemsModalPerPage;
    const endIndex = startIndex + itemsModalPerPage;
    return orderDetails.slice(startIndex, endIndex);
  }, [orderDetails, itemsModalPage, itemsModalPerPage]);

  const getOrderDetailsTotalPages = useMemo(() => {
    return Math.ceil(orderDetails.length / itemsModalPerPage);
  }, [orderDetails.length, itemsModalPerPage]);

  // PROMAX PERFORMANCE: Zero-delay detail change with immediate UI updates
  const handleDetailChange = useCallback((index, field, value) => {
    // Early validation for numeric fields
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value !== '' && value !== null && value !== undefined) {
        if (!/^\d*\.?\d*$/.test(value)) return;
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) return;
      }
    }

    // Update state immediately for instant UI feedback
    const updatedDetails = orderDetailsRef.current.slice();
    const currentDetail = updatedDetails[index];
    updatedDetails[index] = { ...currentDetail, [field]: value };

    // Immediate calculation for affected row only
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = Math.max(0, parseFloat(detail.Quantity) || 0);
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);
      const amount = quantity * rate;

      let discountAmount, discountPct;
      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, parseFloat(value) || 0);
        discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
      } else {
        discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
        discountAmount = (amount * discountPct) / 100;
      }

      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;

      const currentFormData = formDataRef.current;
      const invoiceType = (currentFormData.InvoiceTypeName || '').toLowerCase();
      const useIGST = invoiceType.includes('igst');

      Object.assign(updatedDetails[index], {
        Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount
      });
    }

    // Update state immediately
    setOrderDetails(updatedDetails);

    // Debounce calculation to prevent excessive updates
    if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
    calculationTimeoutRef.current = setTimeout(() => {
      performCalculations(updatedDetails);
    }, 200);
  }, [performCalculations]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, []);

  // Reset search when modal opens
  useEffect(() => {
    if (open && !isViewMode) {
      setItemSearchValue('');
      if (onFetchItems) {
        onFetchItems('');
      }
    }
  }, [open, isViewMode, onFetchItems]);

  // Handle modal loading state when opening
  useEffect(() => {
    if (open) {
      setModalLoading(false);
    } else {
      setModalLoading(false);
    }
  }, [open]);

  // Recalculate when freight/packing amounts or GST rates change
  const freightPackingRef = useRef({
    FreightAmount: formData.FreightAmount,
    FreightGSTRate: formData.FreightGSTRate,
    PackingAmount: formData.PackingAmount,
    PackingGSTRate: formData.PackingGSTRate
  });

  useEffect(() => {
    const current = {
      FreightAmount: formData.FreightAmount,
      FreightGSTRate: formData.FreightGSTRate,
      PackingAmount: formData.PackingAmount,
      PackingGSTRate: formData.PackingGSTRate
    };

    // Only update if values actually changed
    const hasChanged = Object.keys(current).some(key =>
      freightPackingRef.current[key] !== current[key]
    );

    if (hasChanged) {
      freightPackingRef.current = current;

      if (!isViewMode && orderDetails.length > 0) {
        if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
        calculationTimeoutRef.current = setTimeout(() => {
          performCalculations(orderDetails);
        }, 300);
      }
    }
  }, [formData.FreightAmount, formData.FreightGSTRate, formData.PackingAmount, formData.PackingGSTRate, isViewMode, orderDetails.length]);

  // Recalculate all items when InvoiceType changes
  const invoiceTypeRef = useRef(formData.InvoiceTypeName);
  useEffect(() => {
    // Only update if InvoiceType actually changed
    if (invoiceTypeRef.current !== formData.InvoiceTypeName) {
      invoiceTypeRef.current = formData.InvoiceTypeName;

      if (!isViewMode && orderDetails.length > 0 && formData.InvoiceTypeName) {
        const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
        const useIGST = invoiceType.includes('igst');

        const updatedDetails = orderDetails.map(detail => {
          const gstRate = parseFloat(detail.GSTRate) || 0;
          const taxableAmount = parseFloat(detail.TaxableAmount) || 0;
          const gstAmount = (taxableAmount * gstRate) / 100;
          return {
            ...detail,
            CGSTAmount: useIGST ? 0 : gstAmount / 2,
            SGSTAmount: useIGST ? 0 : gstAmount / 2,
            IGSTAmount: useIGST ? gstAmount : 0
          };
        });

        setOrderDetails(updatedDetails);

        if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
        calculationTimeoutRef.current = setTimeout(() => {
          performCalculations(updatedDetails);
        }, 200);
      }
    }
  }, [formData.InvoiceTypeName, isViewMode, orderDetails.length]);

  // Create items map for O(1) lookups
  const itemsMap = useMemo(() => {
    const map = new Map();
    items.forEach(item => map.set(item.Id, item));
    return map;
  }, [items]);

  const handleItemSelect = useCallback((index, itemId) => {
    const selectedItem = itemsMap.get(parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = orderDetailsRef.current.slice();
      const currentDetail = updatedDetails[index];
      const quantity = parseFloat(currentDetail.Quantity) || 0;
      const rate = selectedItem.PurchaseRate || selectedItem.SaleRate || 0;
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(currentDetail.DiscountPercentage) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;

      const currentFormData = formDataRef.current;
      const invoiceType = (currentFormData.InvoiceTypeName || '').toLowerCase();
      const useIGST = invoiceType.includes('igst');

      Object.assign(updatedDetails[index], {
        ItemId: itemId,
        HSNCode: selectedItem.HSNCode || '',
        GSTRate: gstRate,
        Rate: rate,
        Amount: amount,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount
      });

      setOrderDetails(updatedDetails);

      // Debounce calculation to prevent excessive updates
      if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
      calculationTimeoutRef.current = setTimeout(() => {
        performCalculations(updatedDetails);
      }, 200);
    }
  }, [itemsMap, performCalculations]);

  const addDetailRow = useCallback(() => {
    setOrderDetails(prev => [{
      Id: 0, ItemId: '', HSNCode: '', GSTRate: 0, Quantity: 0, Rate: 0, Amount: 0,
      DiscountPercentage: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true
    }, ...prev]);
  }, []);

  const removeDetailRow = useCallback((index) => {
    const updatedDetails = orderDetailsRef.current.filter((_, i) => i !== index);
    setOrderDetails(updatedDetails);

    // Debounce calculation to prevent excessive updates
    if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
    calculationTimeoutRef.current = setTimeout(() => {
      performCalculations(updatedDetails);
    }, 200);
  }, [performCalculations]);

  const formatCurrency = useCallback((amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, []);

  // Memoized calculations for better performance
  const supplierGSTNo = useMemo(() => {
    if (!formData.SupplierId) return null;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
    return supplier?.GSTNumber || supplier?.GSTNo || supplier?.GSTIN || null;
  }, [formData.SupplierId, suppliers]);

  const supplierInvoiceType = useMemo(() => {
    if (formData.InvoiceTypeName) return formData.InvoiceTypeName;
    if (!formData.SupplierId) return null;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
    return supplier?.InvoiceTypeName || supplier?.InvoiceType || null;
  }, [formData.InvoiceTypeName, formData.SupplierId, suppliers]);

  const supplierDisplayName = useMemo(() => {
    if (formData.MasterName) return formData.MasterName;
    if (!formData.SupplierId) return '';
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
    return supplier?.Name || '';
  }, [formData.MasterName, formData.SupplierId, suppliers]);

  // Styling
  const disabledFieldStyle = {
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#5D4037',
      fontWeight: 700,
      WebkitTextFillColor: '#5D4037'
    }
  };

  const compactInput = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };

  const compactInputRight = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };

  const tinyInput = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'right' },
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  const tinyInputCenter = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'center' },
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          onClose();
        }}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation()
          }
        }}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, width: isMobile ? '100%' : '95vw', maxWidth: '1400px' } }}
      >
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#fff', position: 'relative' }}>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography variant="body1" sx={{ color: '#6b7280' }}>Loading Purchase Order...</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Order Information</Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" label="PO Number" value={formData.PurchaseOrderNo || ''}
                    disabled={true} sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: '#f0f9ff' } }} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" type="date" label="PO Date" value={formData.PurchaseOrderDate || ''}
                    onChange={(e) => handleChange('PurchaseOrderDate', e.target.value)} disabled={isViewMode}
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  {isViewMode ? (
                    <TextField fullWidth size="small" label="Supplier" value={supplierDisplayName || '-'}
                      disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  ) : (
                    <FormControl fullWidth size="small" sx={compactInput}>
                      <InputLabel>Supplier</InputLabel>
                      <Select value={formData.SupplierId || ''} onChange={(e) => handleChange('SupplierId', e.target.value)}
                        disabled={isViewMode} label="Supplier" sx={{ fontSize: '0.8rem' }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
                        {suppliers.map(s => <MenuItem key={s.MasterId || s.Id} value={s.MasterId || s.Id} sx={{ fontSize: '0.75rem', py: 0.5 }}>{s.Name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField fullWidth size="small" label="GST No" value={supplierGSTNo || '-'} disabled
                    sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierGSTNo ? '#f0fdf4' : '#f9fafb' } }} />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField fullWidth size="small" label="Invoice Type" value={supplierInvoiceType || '-'} disabled
                    sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierInvoiceType ? '#fef3c7' : '#f9fafb' } }} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af' }}>Order Items</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {orderDetails.length > 0 && (
                    <Typography
                      variant="caption"
                      onClick={() => {
                        setItemsModalOpen(true);
                        setItemsModalPage(1);
                      }}
                      sx={{
                        color: '#1e40af',
                        cursor: 'pointer',
                        fontWeight: 500,
                        textDecoration: 'underline',
                        fontSize: '0.7rem',
                        '&:hover': { color: '#1d4ed8' }
                      }}
                    >
                      See More ({orderDetails.length} items)
                    </Typography>
                  )}
                  {!isViewMode && formData.SupplierId && modalAction === 'create' && (
                    <Button
                      size="small"
                      startIcon={<History sx={{ fontSize: 14 }} />}
                      onClick={() => fetchPOHistoryForSupplier(formData.SupplierId)}
                      variant="text"
                      sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#7c3aed' }}
                    >
                      History
                    </Button>
                  )}
                  {!isViewMode && (
                    <>
                      <Button
                        size="small"
                        startIcon={<List sx={{ fontSize: 14 }} />}
                        onClick={handleOpenGetItemsModal}
                        variant="text"
                        sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#059669' }}
                      >
                        Get Items
                      </Button>
                      <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addDetailRow}
                        variant="outlined" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}>Add</Button>
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
                <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '3%' }}>#</th>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '20%' }}>Item</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>HSN</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>Qty</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%' }}>Rate</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>Disc%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%' }}>DiscAmt</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Taxable</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>GST%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '9%' }}>GST Amt</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Total</th>
                        {!isViewMode && <th style={{ padding: '8px 4px', width: '6%', background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)', textAlign: 'right', fontWeight: 600, color: '#fff' }}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.slice(0, Math.min(50, orderDetails.length)).map((detail, index) => (
                        <TableRow
                          key={`${detail.Id}-${index}`}
                          detail={detail}
                          index={index}
                          items={items}
                          isViewMode={isViewMode}
                          onDetailChange={handleDetailChange}
                          onItemSelect={handleItemSelect}
                          onRemoveRow={removeDetailRow}
                          formatCurrency={formatCurrency}
                          disabledFieldStyle={disabledFieldStyle}
                          tinyInput={tinyInput}
                          tinyInputCenter={tinyInputCenter}
                        />
                      ))}
                      {orderDetails.length === 0 && (
                        <tr><td colSpan={isViewMode ? 10 : 11} style={{ textAlign: 'center', padding: '16px', color: '#6b7280' }}>No items added</td></tr>
                      )}
                      {orderDetails.length > 50 && (
                        <tr><td colSpan={isViewMode ? 10 : 11} style={{ textAlign: 'center', padding: '8px', color: '#1e40af', fontSize: '0.8rem', fontWeight: 500 }}>
                          Showing 50 of {orderDetails.length} items.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Additional Charges</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Freight" value={formData.FreightAmount || ''}
                        onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Packing" value={formData.PackingAmount || ''}
                        onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Instruction 1" value={formData.Instruction1 || ''}
                        onChange={(e) => handleChange('Instruction1', e.target.value)} disabled={isViewMode} multiline rows={1} sx={compactInput} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Instruction 2" value={formData.Instruction2 || ''}
                        onChange={(e) => handleChange('Instruction2', e.target.value)} disabled={isViewMode} multiline rows={1} sx={compactInput} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''}
                        onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} multiline rows={2} sx={compactInput} />
                    </Grid>
                    {/* Audit Information - under Remarks */}
                    {(modalAction === 'view' || modalAction === 'edit') && (
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                          <span style={{ display: 'block', marginBottom: '4px' }}>
                            <span style={{ color: '#64748b' }}>Created by </span>
                            <span style={{ color: '#1e40af', fontWeight: 600 }}>{formData.CreatedByName || '-'}</span>
                            <span style={{ color: '#64748b' }}> on </span>
                            <span style={{ color: '#059669', fontWeight: 500 }}>{formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                          </span>
                          <span style={{ display: 'block' }}>
                            <span style={{ color: '#64748b' }}>Modified by </span>
                            <span style={{ color: '#dc2626', fontWeight: 600 }}>{formData.ModifiedByName || '-'}</span>
                            <span style={{ color: '#64748b' }}> on </span>
                            <span style={{ color: '#f59e0b', fontWeight: 500 }}>{formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                          </span>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>Order Summary</Typography>
                  <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1.5, border: '1px solid #e2e8f0' }}>
                    {[
                      { label: 'Sub Total', value: formData.Amount },
                      { label: 'Discount', value: formData.DiscountAmount, isNeg: true },
                      { label: 'Taxable', value: formData.TaxableAmount },
                      // Show CGST/SGST or IGST based on InvoiceType
                      ...(isIGSTInvoice() ? [
                        { label: 'IGST', value: formData.IGSTAmount },
                      ] : [
                        { label: 'CGST', value: formData.CGSTAmount },
                        { label: 'SGST', value: formData.SGSTAmount },
                      ]),
                      ...(parseFloat(formData.FreightAmount) > 0 ? [
                        { label: 'Freight', value: parseFloat(formData.FreightAmount) || 0 },
                        { label: `Freight GST (${formData.FreightGSTRate || 0}%)`, value: parseFloat(formData.FreightGSTAmount) || 0 },
                      ] : []),
                      ...(parseFloat(formData.PackingAmount) > 0 ? [
                        { label: 'Packing', value: parseFloat(formData.PackingAmount) || 0 },
                        { label: `Packing GST (${formData.PackingGSTRate || 0}%)`, value: parseFloat(formData.PackingGSTAmount) || 0 },
                      ] : []),
                      { label: 'Round Off', value: formData.RoundOff },
                    ].map((row, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: i === 5 ? '1px solid #d1d5db' : 'none' }}>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.85rem' }}>{row.label}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: row.isNeg ? '#dc2626' : '#374151', fontSize: '0.85rem' }}>
                          {row.isNeg ? '- ' : ''}{formatCurrency(row.value)}
                        </Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 1, borderTop: '2px solid #1e40af' }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '1rem' }}>Grand Total:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '1.1rem' }}>{formatCurrency(formData.TotalAmount)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              fontSize: '0.8rem',
              px: 2,
              py: 0.75,
              textTransform: 'none',
              '&:hover': { borderColor: '#9ca3af', bgcolor: '#f3f4f6' }
            }}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button
              onClick={onSave}
              variant="contained"
              size="small"
              disabled={loading || !formData.SupplierId || orderDetails.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                  boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {modalAction === 'create' ? 'Create Order' : 'Update Order'}
            </Button>
          )}
          {isViewMode && !formData.IsVerified && !formData.IsConverted && (
            <Button
              onClick={() => setVerifyDialogOpen(true)}
              variant="contained"
              size="small"
              disabled={loading}
              startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
              sx={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669, #047857)',
                  boxShadow: '0 4px 8px rgba(16, 185, 129, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              Verify
            </Button>
          )}
          {isViewMode && formData.IsVerified && !formData.IsConverted && (
            <Button
              onClick={() => setConvertDialogOpen(true)}
              variant="contained"
              size="small"
              disabled={loading}
              startIcon={<SwapHoriz sx={{ fontSize: 16 }} />}
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                  boxShadow: '0 4px 8px rgba(139, 92, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              Convert to Purchase
            </Button>
          )}
        </DialogActions>

      </Dialog>

      {/* Verify Confirmation Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>Verify Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
            Are you sure you want to verify this Purchase Order?
          </Typography>
          <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 500 }}>
            Note: Once verified, this order cannot be modified.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifyDialogOpen(false)} size="small" variant="outlined">Cancel</Button>
          <Button onClick={() => { onVerify(); setVerifyDialogOpen(false); }} size="small" variant="contained"
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>Verify</Button>
        </DialogActions>
      </Dialog>

      {/* Convert Confirmation Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHoriz sx={{ color: '#8b5cf6', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>Convert to Purchase</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Are you sure you want to convert this Purchase Order to Purchase?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConvertDialogOpen(false)} size="small" variant="outlined">Cancel</Button>
          <Button onClick={() => { onConvert(); setConvertDialogOpen(false); }} size="small" variant="contained"
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>Convert</Button>
        </DialogActions>
      </Dialog>

      {/* See More Items Modal */}
      <Dialog
        open={itemsModalOpen}
        onClose={() => setItemsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', m: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Order Items ({orderDetails.length} total)
              </Typography>
            </Box>
            <IconButton onClick={() => setItemsModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>#</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%' }}>Item</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%' }}>HSN</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Qty</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Rate</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Disc%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Taxable</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>GST%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>GST Amt</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedOrderDetails.map((detail, index) => {
                    const actualIndex = (itemsModalPage - 1) * itemsModalPerPage + index;
                    return (
                      <tr key={actualIndex} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500 }}>{actualIndex + 1}</td>
                        <td style={{ padding: '8px 4px', fontWeight: 500 }}>
                          {detail.ItemName || items.find(i => i.Id === parseInt(detail.ItemId))?.Name || '-'}
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500 }}>{detail.Quantity || 0}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>{detail.DiscountPercentage || 0}%</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(detail.TaxableAmount)}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500 }}>{detail.GSTRate || 0}%</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700, color: '#1B5E20' }}>
                          {formatCurrency((parseFloat(detail.CGSTAmount) || 0) + (parseFloat(detail.SGSTAmount) || 0) + (parseFloat(detail.IGSTAmount) || 0))}
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                          {formatCurrency(detail.TotalAmount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Box>
          {getOrderDetailsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={getOrderDetailsTotalPages}
                page={itemsModalPage}
                onChange={(e, page) => setItemsModalPage(page)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: isMobile ? 0 : 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#059669', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Get Items ({filteredItems.length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name or HSN code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#6b7280' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiInputBase-input': { py: 1 } }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 10).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                    {selectedItems.length > 10 && (
                      <Chip label={`+${selectedItems.length - 10} more`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems.length && getPaginatedItems.length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems.forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems.forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                          />
                        </th>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%' }}>Item</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%' }}>HSN</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>GST%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Qty</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Rate</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Disc%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Disc Amt</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems.map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInOrder(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isSelected ? '#f0f9ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                            opacity: isAlreadyAdded ? 0.5 : 1
                          }}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '4px', fontWeight: 500 }}>
                              {item.Name}
                              {isAlreadyAdded && (
                                <Chip label="Added" size="small" color="success" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{item.HSNCode || '-'}</td>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{item.GSTRate || 0}%</td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{ width: 60, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'center' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 80, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'right' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af' }}>
                                  {formatCurrency(item.PurchaseRate || item.SaleRate || 0)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountPercentage}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                  inputProps={{ min: 0, max: 100 }}
                                  sx={{ width: 60, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'center' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountAmount}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 80, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'right' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const amount = qty * rate;
                                  const finalDiscAmt = discAmt > 0 ? discAmt : (amount * discPct) / 100;
                                  const taxable = amount - finalDiscAmt;
                                  const gstAmt = (taxable * (item.GSTRate || 0)) / 100;
                                  return formatCurrency(taxable + gstAmt);
                                })()
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={getTotalPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small">Cancel</Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            size="small"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
          >
            {addingItemsLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Adding...
              </>
            ) : (
              `Add Selected (${selectedItems.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PO History Modal */}
      <Dialog
        open={poHistoryModalOpen}
        onClose={closeHistoryModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: isMobile ? 0 : 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History sx={{ color: '#7c3aed', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Purchase Order History
              </Typography>
            </Box>
            <IconButton onClick={closeHistoryModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {poHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading history...</Typography>
            </Box>
          ) : poHistoryData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
              <Typography variant="body2">No purchase order history found for this supplier.</Typography>
            </Box>
          ) : (
            <>
              {/* Selected Items Summary */}
              {selectedHistoryItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fbbf24' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', mb: 1, display: 'block' }}>
                    Selected Items ({selectedHistoryItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedHistoryItems.slice(0, 10).map((item, index) => (
                      <Chip
                        key={`${item.poId}-${item.ItemId}-${index}`}
                        label={`${items.find(i => i.Id === parseInt(item.ItemId))?.Name || 'Unknown'} (${item.Quantity})`}
                        size="small"
                        onDelete={() => toggleHistoryItemSelection(item, item.poId)}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                    {selectedHistoryItems.length > 10 && (
                      <Chip label={`+${selectedHistoryItems.length - 10} more`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              )}

              {/* History List */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Found {poHistoryData.length} previous orders
                </Typography>
                <Button
                  size="small"
                  onClick={toggleSelectAllHistoryItems}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {selectedHistoryItems.length === getAllHistoryItems().length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              {poHistoryData.map((po) => (
                <Box key={po.Id} sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                  {/* PO Header */}
                  <Box sx={{
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Checkbox
                        size="small"
                        checked={isPOFullySelected(po.Id)}
                        indeterminate={isPOPartiallySelected(po.Id)}
                        onChange={() => togglePOSelection(po.Id)}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          PO #{po.PurchaseOrderNo}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Date: {new Date(po.PurchaseOrderDate).toLocaleDateString('en-IN')} |
                          Items: {(po.PurchaseOrderDetails || []).length} |
                          Total: {formatCurrency(po.TotalAmount)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedHistoryPOId(expandedHistoryPOId === po.Id ? null : po.Id)}
                    >
                      {expandedHistoryPOId === po.Id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  {/* PO Items */}
                  <Collapse in={expandedHistoryPOId === po.Id}>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#f1f5f9' }}>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '5%' }}>
                              <Checkbox
                                size="small"
                                checked={isPOFullySelected(po.Id)}
                                indeterminate={isPOPartiallySelected(po.Id)}
                                onChange={() => togglePOSelection(po.Id)}
                              />
                            </th>
                            <th style={{ padding: '6px 4px', textAlign: 'left', fontWeight: 600, color: '#374151', width: '25%' }}>Item</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '8%' }}>Qty</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '10%' }}>Rate</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '8%' }}>Disc%</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '10%' }}>Taxable</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '8%' }}>GST%</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '12%' }}>Total</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '10%' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(po.PurchaseOrderDetails || []).map((historyItem, index) => {
                            const isSelected = isHistoryItemSelected(historyItem, po.Id);
                            const isAlreadyAdded = isItemAlreadyInOrder(historyItem.ItemId);

                            return (
                              <tr key={`${po.Id}-${historyItem.ItemId}-${index}`} style={{
                                borderBottom: '1px solid #f1f5f9',
                                background: isSelected ? '#fef3c7' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                                opacity: isAlreadyAdded ? 0.6 : 1
                              }}>
                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    disabled={isAlreadyAdded}
                                    onChange={() => toggleHistoryItemSelection(historyItem, po.Id)}
                                  />
                                </td>
                                <td style={{ padding: '4px', fontWeight: 500 }}>
                                  {items.find(i => i.Id === parseInt(historyItem.ItemId))?.Name || 'Unknown Item'}
                                  {isAlreadyAdded && (
                                    <Chip label="Added" size="small" color="success" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                                  )}
                                </td>
                                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500 }}>{historyItem.Quantity}</td>
                                <td style={{ padding: '4px', textAlign: 'right' }}>{formatCurrency(historyItem.Rate)}</td>
                                <td style={{ padding: '4px', textAlign: 'center' }}>{historyItem.DiscountPercentage || 0}%</td>
                                <td style={{ padding: '4px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(historyItem.TaxableAmount)}</td>
                                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500 }}>{historyItem.GSTRate || 0}%</td>
                                <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                                  {formatCurrency(historyItem.TotalAmount)}
                                </td>
                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                  {isAlreadyAdded ? (
                                    <Chip label="In Order" size="small" color="success" sx={{ fontSize: '0.6rem' }} />
                                  ) : isSelected ? (
                                    <Chip label="Selected" size="small" color="warning" sx={{ fontSize: '0.6rem' }} />
                                  ) : (
                                    <Chip label="Available" size="small" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={closeHistoryModal} variant="outlined" size="small">Cancel</Button>
          <Button
            onClick={addSelectedHistoryItems}
            variant="contained"
            size="small"
            disabled={selectedHistoryItems.length === 0}
            sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
          >
            Add Selected ({selectedHistoryItems.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PurchaseOrderModal;